/**
 * GuruTasksDialog — manager view of any Guru's tasks (Module 10 Addendum §3).
 *
 * Opened from the Gurus list ("Manage Tasks" action). Uses the same
 * canonical task rows the Guru sees on their own My Guru page — no
 * duplicate collection. Requires can_manage_guru_tasks on the backend
 * side (403 if the caller lacks the permission).
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Loader2, Plus, ClipboardList } from 'lucide-react';
import { TaskRow, TaskEditorDialog } from './MyTodoCard';

const API = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = 'gleam_auth_token';

const GuruTasksDialog = ({ open, onClose, guru }) => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);

  const fetchTasks = useCallback(async () => {
    if (!guru?.id) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/admin/gurus/${guru.id}/tasks`, {
        headers: { Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` },
      });
      setTasks(r.data?.tasks || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load tasks');
      setTasks([]);
    } finally { setLoading(false); }
  }, [guru?.id]);

  useEffect(() => { if (open) fetchTasks(); }, [open, fetchTasks]);

  const handleChanged = (updated, deletedId) => {
    if (deletedId) {
      setTasks(prev => prev.filter(t => t.id !== deletedId));
      return;
    }
    if (!updated) return;
    setTasks(prev => {
      const exists = prev.some(t => t.id === updated.id);
      return exists ? prev.map(t => t.id === updated.id ? updated : t) : [updated, ...prev];
    });
  };

  const active = tasks.filter(t => t.status !== 'archived');
  const openCount = active.filter(t => t.status !== 'done').length;

  return (
    <>
      <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="guru-tasks-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardList className="w-5 h-5 text-[#F56F05]" />
              {guru?.full_name ? `${guru.full_name.split(' ')[0]}'s Tasks` : 'Guru Tasks'}
              <span className="text-sm font-normal text-zinc-400" data-testid="guru-tasks-count">
                ({active.length})
              </span>
            </DialogTitle>
            <DialogDescription>
              Assign, prioritize and track this Guru&apos;s tasks. Changes sync in real time to their My Guru page.
            </DialogDescription>
          </DialogHeader>

          <div className="flex items-center justify-between border-b border-zinc-100 pb-2 mb-2">
            <p className="text-[11px] text-zinc-500">
              {openCount === 0
                ? 'No open tasks. Nice work.'
                : `${openCount} open · ${active.length - openCount} completed`}
            </p>
            <Button size="sm" onClick={() => { setEditingTask(null); setEditorOpen(true); }} className="bg-[#F56F05] hover:bg-orange-600" data-testid="add-guru-task-btn">
              <Plus className="w-3.5 h-3.5 mr-1.5" /> Add Task
            </Button>
          </div>

          <div className="flex-1 overflow-y-auto pr-1">
            {loading ? (
              <div className="py-12 text-center text-zinc-400">
                <Loader2 className="w-5 h-5 animate-spin mx-auto" />
              </div>
            ) : active.length === 0 ? (
              <div className="py-12 text-center">
                <ClipboardList className="w-8 h-8 text-zinc-300 mx-auto mb-2" />
                <p className="text-sm font-semibold text-zinc-700">No tasks yet.</p>
                <p className="text-[12px] text-zinc-500 mt-1">Delegate the first item to get started.</p>
              </div>
            ) : (
              <div>
                {active.map(t => (
                  <TaskRow
                    key={t.id}
                    task={t}
                    canManage
                    onChange={handleChanged}
                    onOpen={(task) => { setEditingTask(task); setEditorOpen(true); }}
                  />
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <TaskEditorDialog
        open={editorOpen}
        onClose={() => { setEditorOpen(false); setEditingTask(null); }}
        task={editingTask}
        forGuruUserId={editingTask ? null : guru?.id}
        onSaved={(t) => handleChanged(t)}
      />
    </>
  );
};

export default GuruTasksDialog;
